import { setSessionLastActivityAtMs } from '@app-store'
import { getSessionPolicyStore } from './registry'
import {
  broadcastSessionActivity,
  subscribeSessionActivityBroadcast,
} from './sessionCrossTab'
import {
  forceResetIdleTimerFromExternalActivity,
  resetIdleTimerFromExternalActivity,
} from './sessionIdleTimerBridge'

const THROTTLE_MS = 1000

let lastThrottle = 0
let remoteListenerStarted = false

/** While the inactivity modal is open, ignore local activity (mouse/API) so the idle timer can expire. */
let sessionPromptOpen = false

export function setSessionPromptOpen(open: boolean): void {
  sessionPromptOpen = open
}

function ensureRemoteActivityListener(): void {
  if (remoteListenerStarted) return
  remoteListenerStarted = true
  subscribeSessionActivityBroadcast((ts) => {
    const store = getSessionPolicyStore()
    const cur = store.getState().session.lastActivityAtMs ?? 0
    if (ts > cur) {
      store.dispatch(setSessionLastActivityAtMs(ts))
    }
    resetIdleTimerFromExternalActivity()
  })
}

/** Persisted epoch ms from Redux (redux-persist). */
export function getLastActivityMs(): number {
  const ms = getSessionPolicyStore().getState().session.lastActivityAtMs
  return ms && ms > 0 ? ms : Date.now()
}

/**
 * UI or marked API activity. Throttled; updates persisted Redux + cross-tab broadcast;
 * resets react-idle-timer when API activity happens in this tab or another.
 */
export function recordSessionActivity(): void {
  if (sessionPromptOpen) return
  ensureRemoteActivityListener()
  const now = Date.now()
  if (now - lastThrottle < THROTTLE_MS) return
  lastThrottle = now
  const store = getSessionPolicyStore()
  store.dispatch(setSessionLastActivityAtMs(now))
  broadcastSessionActivity(now)
  resetIdleTimerFromExternalActivity()
}

export function seedSessionActivityNow(): void {
  ensureRemoteActivityListener()
  const now = Date.now()
  lastThrottle = now
  const store = getSessionPolicyStore()
  store.dispatch(setSessionLastActivityAtMs(now))
  broadcastSessionActivity(now)
  forceResetIdleTimerFromExternalActivity()
}

/**
 * "Continue session" on the inactivity prompt. Not throttled — always updates Redux,
 * broadcasts, and resets the idle timer so every tab receives the message and can
 * dismiss the prompt (see SessionIdleCountdownProvider broadcast subscription).
 */
export function recordSessionPromptContinue(): void {
  sessionPromptOpen = false
  ensureRemoteActivityListener()
  const now = Date.now()
  lastThrottle = now
  const store = getSessionPolicyStore()
  store.dispatch(setSessionLastActivityAtMs(now))
  broadcastSessionActivity(now)
  forceResetIdleTimerFromExternalActivity()
}

export function persistSessionActivityTimestampLocal(): void {
  const now = Date.now()
  lastThrottle = now
  getSessionPolicyStore().dispatch(setSessionLastActivityAtMs(now))
}
